'use client';

import React, { createContext, useContext, useReducer, useCallback } from 'react';

interface CartItem {
  id: number;
  quantity: number;
  variationId?: number;
}

interface CartState {
  items: CartItem[];
  itemCount: number;
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: { id: number; quantity?: number; variationId?: number } }
  | { type: 'REMOVE_ITEM'; payload: { id: number } }
  | { type: 'UPDATE_QUANTITY'; payload: { id: number; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'LOAD_CART'; payload: CartItem[] };

const initialState: CartState = {
  items: [],
  itemCount: 0,
};

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { id, quantity = 1, variationId } = action.payload;
      const existingItemIndex = state.items.findIndex(
        item => item.id === id && item.variationId === variationId
      );

      let newItems;
      if (existingItemIndex > -1) {
        newItems = state.items.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        const newItem: CartItem = {
          id,
          quantity,
          variationId,
        };
        newItems = [...state.items, newItem];
      }

      const itemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

      return {
        ...state,
        items: newItems,
        itemCount,
      };
    }

    case 'REMOVE_ITEM': {
      const newItems = state.items.filter(item => item.id !== action.payload.id);
      const itemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

      return {
        ...state,
        items: newItems,
        itemCount,
      };
    }

    case 'UPDATE_QUANTITY': {
      const { id, quantity } = action.payload;
      if (quantity <= 0) {
        return cartReducer(state, { type: 'REMOVE_ITEM', payload: { id } });
      }

      const newItems = state.items.map(item =>
        item.id === id ? { ...item, quantity } : item
      );

      const itemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);
      return {
        ...state,
        items: newItems,
        itemCount,
      };
    }

    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
        itemCount: 0,
      };

    case 'LOAD_CART':
      const items = action.payload;
      const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

      return {
        ...state,
        items,
        itemCount,
      };

    default:
      return state;
  }
}

const CartContext = createContext<{
  state: CartState;
  dispatch: React.Dispatch<CartAction>;
  addItem: (id: number, quantity?: number, variationId?: number) => void;
  removeItem: (id: number) => void;
  updateQuantity: (id: number, quantity: number) => void;
  clearCart: () => void;
} | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  // const [isClient, setIsClient] = React.useState(false);

  // Ensure we're on the client side
  // useEffect(() => {
  //   setIsClient(true);
  // }, []);

  // Il carrello è gestito da WooCommerce, non più da localStorage
  // Il carrello in memoria (state) è solo temporaneo per la UI prima della sincronizzazione

  const addItem = useCallback((id: number, _quantity = 1, _variationId?: number) => {
    // ID prodotti Places subscription (221-226)
    const PLACES_PRODUCT_IDS = [221, 222, 223, 224, 225, 226];
    
    // Se stai cercando di aggiungere un prodotto Places
    if (PLACES_PRODUCT_IDS.includes(id)) {
      // Verifica se c'è già un prodotto Places nel carrello
      const hasPlacesProduct = state.items.some(item => PLACES_PRODUCT_IDS.includes(item.id));
      
      if (hasPlacesProduct) {
        alert('Hai già un abbonamento Places nel carrello. Completa l\'acquisto o rimuovi l\'abbonamento esistente prima di aggiungerne un altro.');
        return;
      }
    }
    
    // Aggiungi direttamente a WooCommerce usando form POST (più affidabile)
    const wpUrl = process.env.NEXT_PUBLIC_WP_URL || '';
    if (wpUrl) {
      // Usa un form POST invece di URL query string per evitare duplicazioni
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `${wpUrl}cart`;
      form.style.display = 'none';
      
      // Campo add-to-cart
      const productInput = document.createElement('input');
      productInput.type = 'hidden';
      productInput.name = 'add-to-cart';
      productInput.value = id.toString();
      form.appendChild(productInput);
      
      // Campo quantity (solo se > 1, altrimenti WooCommerce usa default 1)
      if (_quantity > 1) {
        const quantityInput = document.createElement('input');
        quantityInput.type = 'hidden';
        quantityInput.name = 'quantity';
        quantityInput.value = _quantity.toString();
        form.appendChild(quantityInput);
      }
      
      // Campo variation_id se presente
      if (_variationId) {
        const variationInput = document.createElement('input');
        variationInput.type = 'hidden';
        variationInput.name = 'variation_id';
        variationInput.value = _variationId.toString();
        form.appendChild(variationInput);
      }
      
      // Aggiungi form al body e invialo
      document.body.appendChild(form);
      form.submit();
    } else {
      // Fallback: aggiungi solo al carrello locale se WooCommerce non è configurato
      dispatch({ type: 'ADD_ITEM', payload: { id, quantity: _quantity, variationId: _variationId } });
    }
  }, [state.items]);

  const removeItem = useCallback((id: number) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { id } });
  }, []);

  const updateQuantity = useCallback((id: number, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, []);

  return (
    <CartContext.Provider
      value={{
        state,
        dispatch,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}